import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import AddEntity from "components/Entity/addMoreEntity";
import { US } from "constants/countryCodes";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import React from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  getUpdateChatQNAAnswerCacheKey,
  useUpdateChatQNAMutation,
} from "store/apis/chatQnA";
import { useCreateEntityMutation } from "store/apis/group";
import { BackendError } from "types/utils/error";

type Props = {
  uuid: string;
  requestId: string;
  groupId: string;
  currentAnswer: string | null;
  isDisabled: boolean;
  onSave?: () => void;
};

type NewEntityData = {
  company_name: string;
  formation_type_id: string;
  country_id: string;
  state_id: string;
  entity_type: string;
};

export const ForeignSubsidiaryAnswerCombobox = ({
  uuid,
  requestId,
  groupId,
  currentAnswer,
  isDisabled,
  onSave,
}: Props) => {
  const { alertToast, successToast } = useToast();
  const [updateAnswer] = useUpdateChatQNAMutation({
    fixedCacheKey: getUpdateChatQNAAnswerCacheKey(requestId),
  });
  const [createEntity, { isLoading: isCreating }] = useCreateEntityMutation();
  const addEntityModal = useModal();

  const { entities } = useCurrentChatGroupContext();

  const foreignEntities = entities.filter(
    (entity) => entity.country_code !== US
  );

  const options = foreignEntities.map((entity) => ({
    label: entity.name,
    value: entity.name,
  }));

  const selected = options.find((o) => o.value === currentAnswer) ?? null;

  const saveAnswer = async (answer: string) => {
    try {
      await updateAnswer({
        answerRequestId: uuid,
        requestedAnswer: answer,
        requestId,
        groupId,
      }).unwrap();
      onSave?.();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to save selection",
      });
    }
  };

  const handleChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (!option || Array.isArray(option)) return;
    void saveAnswer((option as OptionData).value);
  };

  const addEntityData = async (newEntity: NewEntityData) => {
    try {
      const entity = await createEntity({
        groupId,
        payload: newEntity,
      }).unwrap();
      await saveAnswer(entity.name);
      successToast({ message: "Subsidiary created successfully" });
      addEntityModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <>
      <Combobox
        options={options}
        value={selected}
        onChange={handleChange}
        isDisabled={isDisabled || isCreating}
        placeholder="Select entity..."
        menuPortalTarget={document.body}
        actions={
          !isDisabled ? (
            <Button
              type="button"
              customType="link"
              onClick={addEntityModal.open}
            >
              Add new foreign subsidiary
            </Button>
          ) : undefined
        }
      />
      <AddEntity
        addEntityData={addEntityData}
        showAddEntity={addEntityModal.isOpen}
        setShowAddEntity={addEntityModal.close}
        isCreating={isCreating}
        modalTitle="Add Foreign Subsidiary"
        skipCountry={["US"]}
      />
    </>
  );
};
